import { useState } from "react";
import { Folder, FolderOpen, ChevronRight, ChevronDown } from "lucide-react";
import { flattenTree, prettyFolderLabel, type TreeNode, type FlatNode } from "@/lib/folderTree";
import { cn } from "@/lib/utils";

interface FolderTreeProps {
  tree: TreeNode[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  countsById: Record<string, number>;
  totalCount: number;
  readOnly?: boolean;
  /** Eintrag „Alle Ordner“ oben (Standard an). */
  showAll?: boolean;
  /** Gewählten Ordner selbst auch aufklappen (Katalog: Hauptordner zeigt seine Unterordner). */
  expandSelected?: boolean;
}

function withAncestors(set: Set<string>, node: FlatNode | undefined, includeSelf = false): Set<string> {
  if (!node) return set;
  const next = new Set(set);
  for (const id of node.ancestorIds) next.add(String(id));
  if (includeSelf) next.add(node.id);
  return next;
}

export function FolderTree({
  tree,
  selectedId,
  onSelect,
  countsById,
  totalCount,
  showAll = true,
  expandSelected = false,
}: FolderTreeProps) {
  const flat = flattenTree(tree);
  const selectedNode = selectedId === null ? undefined : flat.find((n) => n.folderId === selectedId);

  // Vorfahren des gewaehlten Ordners aufklappen – beim Start, wenn sich die Auswahl aendert und
  // wenn der Knoten erst spaeter im Baum erscheint (Ordner laden). Danach bleibt manuelles
  // Zuklappen bestehen, auch bei einem neuen, gleichwertigen Baum.
  const expandKey = selectedNode ? `${selectedId}` : `${selectedId}?`;
  const [expanded, setExpanded] = useState<Set<string>>(() => withAncestors(new Set(), selectedNode, expandSelected));
  const [expandedFor, setExpandedFor] = useState(expandKey);
  if (expandKey !== expandedFor) {
    setExpandedFor(expandKey);
    setExpanded((prev) => withAncestors(prev, selectedNode, expandSelected));
  }

  function isVisible(node: FlatNode): boolean {
    return node.ancestorIds.every((id) => expanded.has(String(id)));
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const isRootSelected = selectedNode === undefined;

  return (
    <aside className="rounded-md border border-line bg-panel">
      <div className="px-3 py-2 border-b border-line-2 flex items-center gap-2">
        <span className="text-xs font-display font-semibold uppercase tracking-wide text-ink-2">
          Ordner
        </span>
      </div>
      <ul className="py-1 max-h-[70vh] overflow-y-auto">
        {showAll && <li>
          <button
            onClick={() => onSelect(null)}
            className={cn(
              "w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors",
              isRootSelected
                ? "bg-primary-soft text-primary font-medium"
                : "text-ink-2 hover:bg-panel-2 hover:text-ink",
            )}
          >
            <FolderOpen className="size-3.5 shrink-0" />
            <span className="flex-1 text-left">Alle Ordner</span>
            <span className="font-mono text-[11px] text-muted">{totalCount}</span>
          </button>
        </li>}
        {flat.filter(isVisible).map((node) => {
          const isSelected = node.folderId === selectedId;
          const isExpanded = expanded.has(node.id);
          const count = countsById[node.id] ?? 0;
          return (
            <li key={node.id} className="group">
              <div
                className={cn(
                  "flex items-center gap-1 px-1 py-1 text-sm transition-colors",
                  isSelected
                    ? "bg-primary-soft text-primary font-medium"
                    : "text-ink-2 hover:bg-panel-2 hover:text-ink",
                )}
              >
                <span style={{ paddingLeft: `${node.depth * 12}px` }} />
                {node.hasChildren ? (
                  <button
                    onClick={() => toggleExpand(node.id)}
                    className="inline-flex items-center justify-center size-4 text-muted hover:text-ink"
                  >
                    {isExpanded ? (
                      <ChevronDown className="size-3" />
                    ) : (
                      <ChevronRight className="size-3" />
                    )}
                  </button>
                ) : (
                  <span className="size-4" />
                )}
                <Folder className="size-3.5 shrink-0" />
                <button
                  onClick={() => onSelect(node.folderId)}
                  className="flex-1 text-left truncate"
                  title={node.path.join(" / ")}
                >
                  {prettyFolderLabel(node.name)}
                </button>
                <span className="font-mono text-[11px] text-muted">{count}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
